"use client";

import React, { ReactNode, useCallback } from "react";
import { Handshake } from "lucide-react";
import { SignedIn, SignedOut, useAuth } from "@clerk/nextjs";
import { TradeItem, TradeListType } from "modules/tradeLists/model/tradeList";
import { tradeItemToProposalItem } from "modules/trade-proposals/utils/convertTradeItem";
import { availableQuantity } from "modules/tradeLists/utils/tradeItemAvailability";
import { useChatMessages } from "modules/chat/hooks/useChatMessages";
import { cn } from "utils/utils";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { SignUpButtonCTA } from "modules/auth/components/SignUpButtonCTA";
import {
  tradeReturnUrlForCard,
  useTradeReturnSurface,
} from "modules/common/components/TradeReturnSurface";
import { useProposalPreviewModal } from "modules/trade-proposals/stores/useProposalPreviewStore";
import { captureClientEvent } from "modules/common/analytics/client";
import { POSTHOG_EVENTS } from "modules/common/analytics/events";

export interface ContactUserButtonProps {
  item: TradeItem;
  className?: string;
  size?: "small" | "large";
  disabled?: boolean;
  fromMatch?: boolean;
  text?: string;
  icon?: ReactNode;
}

export function RecentTradesContactButton({
  item,
  className,
  size = "large",
  disabled = false,
  fromMatch = false,
  text,
  icon = <Handshake size={ICON_SIZE} />,
}: ContactUserButtonProps) {
  const { userId, isLoaded } = useAuth();
  const { openDialog } = useProposalPreviewModal();

  const { getOrCreateChat } = useChatMessages();
  const isCreatingChat = getOrCreateChat.isPending;

  // Call sites that pass `text` still win.
  const label = text ?? "Trade";

  // This is the single-card twin of the bulk proposal action, so it answers to
  // the same availability rule: copies already committed
  // to an active proposal can't be offered again. Guarding here rather than at
  // the six call sites keeps the rule in one place.
  //
  // Match rows carry spokenForQuantity in the same unit as their tradeable
  // quantity, so this refuses on matched surfaces too — see the note in
  // tradeItemAvailability.ts.
  const isFullySpokenFor = availableQuantity(item) <= 0;

  // Quantity stays the full listed count on purpose: ProposalPreviewModal
  // computes the real bound itself via computeCapacity, intersecting the
  // spoken-for overlay with what this proposal already holds. Pre-reducing
  // here would double-subtract.
  const proposalItem = tradeItemToProposalItem(item);
  const isWantsList = item.list?.type === TradeListType.WANTS;

  const handleClick = useCallback(async () => {
    if (!userId) return;

    try {
      const chat = await getOrCreateChat.mutateAsync([userId, item.user.id]);
      if (!chat) return;

      openDialog({
        cards: [proposalItem],
        chatId: chat.id,
        currentUserId: userId,
        isUserGiving: isWantsList,
        partnerUsername: item.user.username,
        partnerUserId: item.user.id,
        anyPrinting: item.anyPrinting,
        fromMatch,
        matchIds: item.matchId ? [item.matchId] : undefined,
      });
      if (fromMatch && item.matchId) {
        captureClientEvent(POSTHOG_EVENTS.TRADE_MATCH_ACTIONED, {
          action_type: "proposal_started",
          entry_point: "matched_card",
          match_id: item.matchId,
          game: item.game,
        });
      }
    } catch {
      // Error toast is handled by useChatMessages onError callback
    }
  }, [
    userId,
    item.user.id,
    item.user.username,
    item.anyPrinting,
    item.game,
    item.matchId,
    fromMatch,
    getOrCreateChat,
    openDialog,
    proposalItem,
    isWantsList,
  ]);

  const isPending = isCreatingChat || !isLoaded;

  return isLoaded ? (
    <>
      <SignedIn>
        <button
          onClick={handleClick}
          disabled={isPending || disabled || isFullySpokenFor}
          className={cn(
            "btn btn-primary btn-sm",
            className,
            size === "small" ? "btn-circle" : "w-full"
          )}
          aria-label={
            isFullySpokenFor
              ? "All copies are already in an active trade"
              : label
          }
        >
          {isCreatingChat ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            icon
          )}
          {size === "large" && <span>{label}</span>}
        </button>
      </SignedIn>
      <SignedOut>
        <VisitorTradeCTA
          cardName={item.name}
          className={cn(
            "btn btn-primary btn-sm",
            className,
            size === "small" ? "btn-circle" : "w-full"
          )}
          icon={icon}
        >
          {size === "large" && <span>{label}</span>}
        </VisitorTradeCTA>
      </SignedOut>
    </>
  ) : (
    <button
      className={cn(
        "btn btn-primary btn-sm",
        className,
        size === "small" ? "btn-circle" : "w-full"
      )}
      aria-label={label}
    >
      {icon}
      {size === "large" && <span>{label}</span>}
    </button>
  );
}

/**
 * Anonymous visitors come back to the section they were browsing, filtered to
 * this card. Its own component so the context subscription and the url work
 * only exist inside <SignedOut>: signed-in cells never mount it. Undefined
 * outside a TradeReturnSurface leaves the CTA's "return to the current page"
 * default intact.
 */
function VisitorTradeCTA({
  cardName,
  className,
  icon,
  children,
}: {
  cardName: string;
  className: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  const returnSurface = useTradeReturnSurface();
  return (
    <SignUpButtonCTA
      returnUrl={tradeReturnUrlForCard(cardName, returnSurface ?? {})}
      variant="table"
      className={className}
      size="sm"
      icon={icon}
    >
      {children}
    </SignUpButtonCTA>
  );
}

export default RecentTradesContactButton;
